import {registerTourSteps} from './product-tours.js'

// Walk-through of the ?v=3 patient chart layout. It is launched three ways, all through the shared
// registry launcher in product-tours.js: automatically the first time the layout is shown to a user
// who can see the tour (the server marks the page with `data-chart-v3-auto-tour`), from the
// "Take the tour" button in the test-view banner, and from the tours drawer.
export const PATIENT_CHART_TOUR_ID = 'patient-chart-layout'

const rootSelector = '[data-chart-v3-root]'
const pendingKey = 'cpm:pending-patient-chart-tour'
const pendingLifetime = 30 * 60 * 1000
const wait = ms => new Promise(resolve => setTimeout(resolve, ms))

function savePending(browserWindow) {
    try { browserWindow.sessionStorage.setItem(pendingKey, String(Date.now())) } catch (e) {}
}

function takePending(browserWindow, consume = false) {
    try {
        const savedAt = Number(browserWindow.sessionStorage.getItem(pendingKey))
        const valid = savedAt > 0 && Date.now() - savedAt < pendingLifetime
        if (consume || !valid) browserWindow.sessionStorage.removeItem(pendingKey)
        return valid
    } catch (e) { return false }
}

export function patientChartTourSteps(documentObject, browserWindow) {
    const root = documentObject.querySelector(rootSelector)
    const chart = root && browserWindow.Alpine ? browserWindow.Alpine.$data(root) : null

    if (!chart) {
        return {
            steps: [{
                selector: null,
                title: 'Open a patient chart first',
                text: 'Open any patient chart. CPM will switch to the new layout and start this tour for you.',
            }],
            onEnd: null,
            deferred: true,
            onDefer: () => savePending(browserWindow),
            onCancel: () => takePending(browserWindow, true),
        }
    }

    const startedFolded = chart.desktop && chart.folded

    // The sidebar steps need the sidebar open. On a wide screen an open note is minimised for the
    // duration (the sidebar's own rule) and comes back when the tour ends.
    const guideSection = documentObject.querySelector('[data-cy="welcome-call-guide-section"]')
    const unfold = async () => {
        if (chart.desktop && chart.folded) {
            chart.toggleSidebar()
            await wait(450)
        }
    }

    // The timer's pill names the program the time counts toward: CCM, RPM or CCM/RPM.
    const programLabel = documentObject.querySelector('[data-cy="chart-timer"] [data-cy="chargeable-service-pill"]')?.textContent?.trim() || 'CCM'

    const steps = [
        {
            selector: null,
            title: 'Welcome to the new patient chart',
            text: 'Everything you used before is still here, in a tidier place. This one-minute tour shows where things moved. You can switch back to the classic layout at any time.',
        },
        {
            selector: '[data-cy="header-patient-name"]',
            position: 'bottom',
            title: 'Always know who you are working on',
            text: 'The patient’s name and ID stay in the top bar, even when the sidebar is folded. Hover the copy icon next to the name, date of birth or MRN to grab it for an EHR lookup. The round badge shows their status: green enrolled, red withdrawn, light red withdrawn on first call, orange unreachable, grey paused. Hover it for the exact status.',
        },
        {
            selector: '[data-cy="chart-timer"]',
            position: 'bottom',
            title: `Your ${programLabel} time`,
            text: 'The timer, its health dot and the billing type sit together beside the patient. It works exactly as before.',
        },
        {
            selector: '[data-call-action-buttons], [data-cy="open-notes-button"]',
            position: 'bottom',
            title: 'Roaming, Call and New note',
            text: 'These are now round icon buttons. Hover one to see its name. During a call, the Call button shows End Call and the number.',
        },
        {
            selector: '[data-cy="patient-search-button"]',
            position: 'bottom',
            title: 'Find another patient',
            text: 'Search moved to the icon group on the right, with your schedule, notifications and the tours list. Press ⌘K to open it quickly.',
        },
        {
            selector: '[data-cy="chart-sidebar-toggle"]',
            position: 'right',
            before: unfold,
            title: 'Fold the sidebar for more room',
            text: 'Fold it to a slim icon rail whenever you want a wider chart. A note beside the chart folds the sidebar; maximizing the note opens the full sidebar beside it.',
        },
        {
            selector: '[data-cy="chart-section"]',
            position: 'right',
            before: unfold,
            title: 'Patient chart',
            text: 'The status menu moved here from the top of the page, with the chart tabs below it: Overview, Activities, Care Plan and Patient Profile.',
        },
        {
            selector: '[data-cy="chronic-conditions-section"]',
            position: 'right',
            before: unfold,
            title: 'Chronic conditions',
            text: 'Always in view, so you can check them before every call. Long lists show the first six with a +N more link.',
        },
        // Only on a Nurse Onboarding practice patient: the chart carries the welcome-call script.
        ...(guideSection ? [{
            selector: '#welcome-call-guide-drawer',
            position: 'left',
            before: async () => {
                const guide = browserWindow.Alpine.$data(guideSection)
                if (guide.open) return
                guide.open = true
                await wait(300)
            },
            title: 'Welcome call guide',
            text: 'This simulated patient comes with the full welcome-call script in a drawer on the right: what to say, what to check against her chart, and what to avoid. Close it with the X and reopen it from the tab on the edge. Real patients do not have this.',
        }] : []),
        {
            selector: '[data-cy="call-status"]',
            position: 'right',
            before: unfold,
            title: 'Call status',
            text: 'The next call and the count of consecutive unsuccessful calls. The five-step bar fills as attempts fail. Hover it to see when the patient becomes unreachable.',
        },
        {
            selector: '[data-cy="patient-actions"]',
            position: 'right',
            before: unfold,
            title: 'Patient actions',
            text: 'Send Call Reminder, Add Care Person and Practice’s Supported Insurances moved here from the three-dot menu.',
        },
        {
            selector: '[data-cy="patient-details"]',
            position: 'right',
            before: unfold,
            title: 'Patient details',
            text: 'Date of birth, phone, doctors, practice and family live here. Every section has a title you can click to fold it, and the sidebar remembers what you left open.',
        },
        {
            selector: '[data-cy="chart-v3-back"]',
            position: 'right',
            before: unfold,
            title: 'You can go back any time',
            text: '“Back to classic layout” returns you to the layout you know, and the banner at the top has the same button. Tell us what works and what doesn’t with the Feedback button. You can replay this tour from the tours icon.',
        },
    ]

    // The chart is fully rendered by the time the tour starts, so a control that is missing or
    // hidden belongs to another role: an office admin has no timer, call buttons or patient
    // search. Pointing a step at nothing would describe a feature the viewer cannot see. Steps
    // with a `before` hook open their own target (the folded sidebar) and are always kept.
    const isOnScreen = element => Boolean(element) && (typeof element.getClientRects !== 'function' || element.getClientRects().length > 0)
    const stepsOnThisPage = steps.filter(step => step.selector === null || step.before || isOnScreen(documentObject.querySelector(step.selector)))

    return {
        steps: stepsOnThisPage,
        onEnd: () => {
            if (startedFolded && chart.desktop && !chart.folded) {
                chart.toggleSidebar()
            }
        },
    }
}

export function registerPatientChartTour(browserWindow, documentObject) {
    registerTourSteps(PATIENT_CHART_TOUR_ID, patientChartTourSteps)

    let startedRoot = null
    let readinessAttempts = 0
    const startIfDue = () => {
        const chartRoot = documentObject.querySelector(rootSelector)
        const pending = takePending(browserWindow)

        if (pending && !chartRoot && documentObject.querySelector('[data-patient-chart-root]')) {
            const url = new URL(browserWindow.location.href)
            if (url.searchParams.get('v') === '3') {
                takePending(browserWindow, true)
            } else {
                url.searchParams.set('v', '3')
                browserWindow.location.assign(url.toString())
            }
            return
        }

        if (!chartRoot || startedRoot === chartRoot || (!pending && !chartRoot.hasAttribute('data-chart-v3-auto-tour'))) {
            return
        }

        startedRoot = chartRoot
        browserWindow.setTimeout(() => {
            if (documentObject.querySelector(rootSelector) !== chartRoot) {
                startedRoot = null
                return
            }
            if (!browserWindow.Alpine?.$data(chartRoot)) {
                startedRoot = null
                if (++readinessAttempts < 12) browserWindow.setTimeout(startIfDue, 250)
                return
            }
            readinessAttempts = 0
            if (pending) takePending(browserWindow, true)
            browserWindow.cpmStartTour?.(PATIENT_CHART_TOUR_ID)
        }, 900)
    }

    documentObject.addEventListener('livewire:navigated', startIfDue)
    documentObject.addEventListener('DOMContentLoaded', startIfDue)
    documentObject.addEventListener('alpine:initialized', startIfDue)
    browserWindow.addEventListener('load', startIfDue)
    startIfDue()
}
